import { ModalLayout } from '@/components/ModalLayout'
import { TaskHeaderLeft } from '@/components/TaskHeaderLeft'
import { Button, Modal, State, Toast } from '@seiue/ui'
import { Form } from 'antd'
import {
  Plugin,
  SchoolPlugin,
  SchoolPluginScope,
  SourceEnum,
  pluginApi,
  reflectionApi,
} from 'packages/sdks-next/growp'
import { EntryComponentsWrapper } from 'packages/theme'
import { useEffect, useState } from 'react'

import {
  UserPluginContext,
  getSystemPluginConfigValue,
  handleSavedPluginScopes,
  initSystemPluginConfig,
} from '../utils/util'

import { SystemPluginContent } from './components/SystemPluginContent'

/**
 * 编辑系统应用弹窗
 *
 * @param param - 参数
 * @param param.onClose - 关闭方法
 * @param param.schoolPlugin - 编辑的应用
 * @param param.plugin - 系统应用
 * @returns 组件
 */
export const ApplySystemPluginModal: React.FC<{
  onClose: () => void
  schoolPlugin?: SchoolPlugin
  plugin: Plugin
}> = ({ onClose, plugin, schoolPlugin }) => {
  const isEdit = schoolPlugin !== undefined
  const id = schoolPlugin?.id
  const [form] = Form.useForm()

  const defaultIcon = !schoolPlugin?.icon
    ? plugin.icon ?? undefined
    : schoolPlugin.icon

  const initFormValues =
    schoolPlugin !== undefined
      ? {
          ...schoolPlugin,
          ...initSystemPluginConfig(schoolPlugin, plugin),
          icon: defaultIcon,
        }
      : { icon: defaultIcon }

  const [uploadIcon, setUploadIcon] = useState<string | undefined>(defaultIcon)

  const [scopes, setScopes] = useState<SchoolPluginScope>(
    schoolPlugin?.scopes !== undefined
      ? schoolPlugin.scopes
      : { ids: [], studentIds: [], teacherIds: [] }
  )

  const [showCard, setShowCard] = useState<boolean>(scopes.ids.length > 0)

  const { data: tags, loading } = pluginApi.useQuerySchoolPluginTags()

  const { data: exist, loading: existLoading } =
    pluginApi.useQuerySchoolPlugins({
      paginated: 0,
    })

  const { data: selectInfo, loading: selectLoading } =
    reflectionApi.useCountReflections(
      { idIn: schoolPlugin?.scopes?.ids.join(',') },
      { disable: !isEdit || schoolPlugin.scopes === undefined }
    )

  const [selectNames, setSelectNames] = useState<string[]>(
    isEdit && selectInfo ? selectInfo.names : []
  )

  const { api: createSchoolPlugin } = pluginApi.useCreateSchoolPlugin({
    reload: [pluginApi.querySchoolPlugins, pluginApi.querySchoolPluginTags],
  })
  const { api: updateSchoolPlugin } = pluginApi.useUpdateSchoolPlugin({
    reload: [pluginApi.querySchoolPlugins, pluginApi.querySchoolPluginTags],
  })

  const onClickSave = async () => {
    form.submit()

    form
      .validateFields()
      .then(async (values) => {
        const { config, complated } = getSystemPluginConfigValue(
          { ...initFormValues, ...values },
          plugin
        )

        const saveData = {
          ...initFormValues,
          ...values,
          description: values.description || '',
          entryUrls: {
            web: values.entryUrls.web,
            mobile: values.entryUrls.mobile || '',
          },
          tagId: values.tagId || 0,
          scopes: handleSavedPluginScopes(scopes, showCard),
          sourcePluginId: plugin.id,
          config,
        }

        if (!complated) {
          Toast.error('应用配置未填写完整，请检查')
          return
        }
        if (isEdit && id) {
          await updateSchoolPlugin({ id, payload: saveData })
        } else {
          await createSchoolPlugin({
            source: SourceEnum.System,
            payload: saveData,
          })
        }

        onClose()
        Toast.success(isEdit ? '保存成功' : '添加成功')
      })
      .catch(() => Toast.error('填写内容不符合要求，请检查'))
  }

  const onClickCancel = () => {
    if (form.getFieldValue('label') === undefined) {
      onClose()
      return
    }

    Modal.confirm({
      title: '确认离开该界面吗?',
      content: '正在离开该界面，离开后修改数据不会保存，确认离开吗？',
      onOk: () => onClose(),
    })
  }

  useEffect(() => {
    if (!selectLoading && isEdit && !!selectInfo) {
      setSelectNames(selectInfo.names)
    }
  }, [selectLoading, selectInfo, isEdit])

  if (loading || selectLoading || existLoading) return <State.Loading />

  return (
    <EntryComponentsWrapper>
      <UserPluginContext.Provider
        value={{
          initFormValues,
          uploadIcon,
          setUploadIcon,
          tags,
          scopes,
          setScopes,
          selectNames,
          setSelectNames,
          editPlugin: schoolPlugin,
          exist: exist ?? [],
          showCard,
          setShowCard,
        }}
      >
        <ModalLayout
          title={
            <TaskHeaderLeft
              title={'配置单点登录应用'}
              onClose={onClose}
            />
          }
          headerRight={
            <>
              <Button
                type="gray"
                style={{ marginRight: '12px' }}
                onClick={onClickCancel}
              >
                {'取消'}
              </Button>
              <Button
                type="primary"
                style={{ marginRight: 0 }}
                onClick={onClickSave}
              >
                {'保存'}
              </Button>
            </>
          }
          contentStyle={{
            padding: '20px 240px',
            height: 'calc(100vh - 72px)',
            overflow: 'auto',
          }}
          content={
            <SystemPluginContent
              form={form}
              plugin={plugin}
            />
          }
        />
      </UserPluginContext.Provider>
    </EntryComponentsWrapper>
  )
}
